import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BotanicalSpray } from "../components/Botanical";

const ease = [0.22, 1, 0.36, 1];

const PORTRAIT =
  "https://images.unsplash.com/photo-1731576089270-9e806089a40f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxiYWtlciUyMGRlY29yYXRpbmclMjBjYWtlJTIwZmxvdXIlMjBwYXN0cnklMjBjaGVmJTIwYmFraW5nfGVufDB8fHx8MTc5MTE1MDYwOXww&ixlib=rb-4.1.0&q=85";
const HANDS =
  "https://images.unsplash.com/photo-1670843840225-2ffcaf483c01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxiYWtlciUyMGRlY29yYXRpbmclMjBjYWtlJTIwZmxvdXIlMjBwYXN0cnklMjBjaGVmJTIwYmFraW5nfGVufDB8fHx8MTc5MTE1MDYwOXww&ixlib=rb-4.1.0&q=85";

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [80, -60]);

  return (
    <section id="about" ref={ref} className="relative py-24 md:py-32 bg-[#F3EEE6]/70 overflow-hidden" data-testid="about-section">
      <div className="absolute top-10 right-0 w-64 text-[#C5A059]/30 pointer-events-none rotate-90">
        <BotanicalSpray />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-5 relative order-2 lg:order-1">
          <motion.div style={{ y: y1 }} className="relative z-10 w-3/4 rounded-t-[999px] overflow-hidden shadow-2xl shadow-[#8B5D6C]/25 aspect-[3/4]">
            <img src={PORTRAIT} alt="Lall Bansal at work in the Velvet Bakes studio" className="w-full h-full object-cover" data-testid="about-portrait" />
          </motion.div>
          <motion.div
            style={{ y: y2 }}
            className="absolute -bottom-10 -right-2 sm:right-4 w-1/2 rounded-2xl overflow-hidden border-4 border-[#F9F6F0] shadow-xl aspect-square"
          >
            <img src={HANDS} alt="Piping buttercream details by hand" className="w-full h-full object-cover" data-testid="about-detail-image" />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease }}
          className="lg:col-span-7 order-1 lg:order-2 lg:pl-8"
        >
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#8B5D6C] mb-4">Meet the Baker</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A2834] leading-tight">
            The hands &amp; heart behind Velvet Bakes
          </h2>
          <div className="mt-8 space-y-5 text-[#70505D] leading-relaxed text-sm md:text-base">
            <p>
              I'm Lall Bansal — baker, sugar artist and the one-woman studio behind every
              Velvet Bakes creation. What began as birthday cakes for family became a
              love affair with buttercream, and eventually a little studio built on one
              belief: a celebration cake should taste as beautiful as it looks.
            </p>
            <p>
              Every cake that leaves my kitchen is baked from scratch with proper
              ingredients — real vanilla bean, Belgian chocolate, British butter — and
              finished by hand, from the silkiest ganache to the last sugar petal. No two
              cakes are ever the same, because no two celebrations are.
            </p>
            <p>
              When you order from Velvet Bakes, you deal with me directly — from the
              first sketch to the moment your cake is placed on its table. That's the
              way I like it, and I think you'll taste the difference.
            </p>
          </div>
          <p className="mt-10 font-serif italic text-3xl text-[#8B5D6C]" data-testid="about-signature">Lall x</p>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
